import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  loadAnalytics,
  loadMarketing,
  trackPageview,
  readConsent,
  CONSENT_EVENT,
  type StoredConsent,
} from "@/lib/tracking";
import { sitePageview } from "@/lib/site-analytics";

function applyConsent(c: StoredConsent | null) {
  if (!c) return;
  if (c.analytics) loadAnalytics();
  if (c.marketing) loadMarketing();
}

// [ULTURE] Fuga de memoria de GTM en la SPA. El disparador de clics en enlaces de GTM (GTM-PB94GLKW, que se carga
// siempre, también con la analítica denegada) mete cada clic en window.dataLayer: {event:'gtm.linkClick',
// 'gtm.element': el <a> pulsado, eventCallback: un cierre que captura el PointerEvent y, con él, toda la ruta del
// evento}. dataLayer no se vacía nunca, así que cada página que se dejaba atrás quedaba retenida entera con sus
// listeners (+3.650 nodos y +366 listeners por vuelta a las 5 páginas nuevas). Cuando ese elemento ya no está en el
// documento, GTM hace rato que procesó el evento (lee la entrada al hacer push), así que soltamos el elemento y el
// callback de esa entrada. No se toca nada más: ni el resto del dataLayer ni el modelo interno de GTM.
const GTM_SCRUB_DELAY_MS = 1500;

function releaseDetachedGtmElements() {
  const dl = window.dataLayer;
  if (!Array.isArray(dl)) return;
  for (const entry of dl) {
    if (!entry || typeof entry !== "object") continue;
    const o = entry as Record<string, unknown>;
    const el = o["gtm.element"] as Node | null | undefined;
    if (!el || typeof el !== "object" || !("isConnected" in el) || el.isConnected) continue;
    o["gtm.element"] = null;
    if (typeof o.eventCallback === "function") delete o.eventCallback;
  }
}

export default function TrackingProvider() {
  const location = useLocation();

  useEffect(() => {
    applyConsent(readConsent());
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<StoredConsent>).detail;
      applyConsent(detail ?? readConsent());
      // Fire a pageview right after acceptance so the current route is counted.
      trackPageview(window.location.pathname + window.location.search);
    };
    window.addEventListener(CONSENT_EVENT, onChange as EventListener);
    return () => window.removeEventListener(CONSENT_EVENT, onChange as EventListener);
  }, []);

  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // [ULTURE] Tras cada cambio de ruta, con margen para que GTM procese el clic y la página anterior acabe de
  // desmontarse, soltamos los elementos desconectados que GTM dejó en dataLayer (ver releaseDetachedGtmElements).
  useEffect(() => {
    const id = window.setTimeout(releaseDetachedGtmElements, GTM_SCRUB_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.search]);

  // Our own cookieless counter (/admin/analytics): no consent needed, see site-analytics.ts.
  useEffect(() => {
    sitePageview(location.pathname);
  }, [location.pathname]);

  return null;
}
